import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  ButtonBase,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TableContainer,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckIcon from "@mui/icons-material/Check";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import {
  AnalyticsKpiCard,
  AnalyticsKpiGrid,
} from "./platformConsoleAnalyticsUi";
import { FinanceAiIconButton } from "./FinanceMetricAi";

/** Paleta alinhada a Passagens / Uso do produto. */
export const FINANCE_KPI_ACCENT = {
  revenue: "#2e7d32",
  mrr: "#1976d2",
  arr: "#1565c0",
  growth: "#00838f",
  churn: "#d32f2f",
  retention: "#2e7d32",
  unit: "#6a1b9a",
  cost: "#ed6c02",
  margin: "#455a64",
  positive: "#2e7d32",
  negative: "#d32f2f",
  valuation: "#5e35b1",
  clients: "#0288d1",
  default: "#1976d2",
};

export function formatBrlFromCents(cents) {
  if (cents == null || !Number.isFinite(Number(cents))) return "—";
  return (Number(cents) / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

export function formatPct(v, digits = 1) {
  if (v == null || !Number.isFinite(Number(v))) return "—";
  return `${(Number(v) * 100).toFixed(digits)}%`;
}

export function formatNum(v, digits = 2) {
  if (v == null || !Number.isFinite(Number(v))) return "—";
  return Number(v).toLocaleString("pt-BR", { maximumFractionDigits: digits });
}

export function formatSyncedAt(syncedAt) {
  if (!syncedAt) return null;
  try {
    return new Date(syncedAt).toLocaleString("pt-BR");
  } catch {
    return String(syncedAt);
  }
}

/**
 * Datas do Financeiro sempre em formato Brasil.
 * - YYYY-MM-DD → DD/MM/YYYY
 * - YYYY-MM → MM/YYYY
 * - ISO datetime → DD/MM/YYYY (ou com hora se withTime)
 * - DD/MM/YYYY já formatado → normaliza com zero à esquerda
 * @param {string|Date|null|undefined} raw
 * @param {{ withTime?: boolean, empty?: string }} [opts]
 */
export function formatFinanceDateBr(raw, opts = {}) {
  const empty = opts.empty != null ? opts.empty : "—";
  if (raw == null || raw === "") return empty;
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    return opts.withTime ? raw.toLocaleString("pt-BR") : raw.toLocaleDateString("pt-BR");
  }
  const s = String(raw).trim();
  if (!s) return empty;

  const iso = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?)?/.exec(
    s
  );
  if (iso) {
    const date = `${iso[3]}/${iso[2]}/${iso[1]}`;
    if (opts.withTime && iso[4] != null) return `${date} ${iso[4]}:${iso[5]}`;
    return date;
  }

  const ym = /^(\d{4})-(\d{2})$/.exec(s);
  if (ym) return `${ym[2]}/${ym[1]}`;

  const br = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(s);
  if (br) {
    return `${String(br[1]).padStart(2, "0")}/${String(br[2]).padStart(2, "0")}/${br[3]}`;
  }

  try {
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) {
      return opts.withTime ? d.toLocaleString("pt-BR") : d.toLocaleDateString("pt-BR");
    }
  } catch {
    /* ignore */
  }
  return s;
}

export function FinanceLoading({ label = "Carregando…" }) {
  return (
    <Box
      sx={{ py: 6, display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}
      role="status"
      aria-label={label}
    >
      <CircularProgress size={28} />
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

export function FinanceEmptyState({ title, description, actionLabel, action }) {
  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({
        p: 3,
        textAlign: "center",
        bgcolor: alpha(theme.palette.info.main, 0.04),
        borderStyle: "dashed",
        borderRadius: 2,
      })}
    >
      <Typography variant="subtitle1" sx={{ mb: 0.75, fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520, mx: "auto" }}>
        {description}
      </Typography>
      {action ? (
        <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>{action}</Box>
      ) : actionLabel ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
          {actionLabel}
        </Typography>
      ) : null}
    </Paper>
  );
}

/** Copy canônica — CTA e avisos compartilhados entre abas. */
export const FINANCE_COPY = {
  syncCta: "Use “Atualizar ERP” no topo da página.",
  syncTooltipStatements:
    "Atualiza demonstrações, indicadores e snapshots gerenciais a partir do ERP para o período selecionado.",
  syncTooltipLedger:
    "Atualiza lançamentos e contas do caixa a partir do ERP para o período selecionado.",
  syncTooltipAccounts: "Importa o cadastro de contas bancárias a partir do ERP.",
  emptyMonth: "Ainda sem dados deste mês",
  emptyYear: "Ainda sem dados deste ano",
  noSnapshot:
    "Não há snapshot financeiro para este período. Atualize o ERP no topo da página para montar os números.",
  basisFallback:
    "Este período ainda não separa competência e caixa. Os números seguem o regime disponível — atualize o ERP para comparar os dois com precisão.",
  basisCompetenceHint:
    "Competência: mês de referência da cobrança/despesa (do que o lançamento se refere). Vencimento e data de pagamento não mudam o mês.",
  basisCashHint:
    "Caixa: somente o que realmente entrou ou saiu no mês (liquidação efetiva). Previsões em aberto não entram.",
  dualBasisStale:
    "As datas de competência e caixa ainda não estão separadas neste snapshot. Atualize o ERP para o regime contábil funcionar por completo.",
  dualBasisSame:
    "Neste período, liquidação e competência caem no mesmo mês — os totais dos dois regimes podem coincidir.",
  partialYear: (n) =>
    n != null
      ? `Ano parcial: ${n} de 12 meses com dados. Os totais refletem só o que já foi sincronizado.`
      : "Ano parcial: faltam meses com dados sincronizados.",
};

export function FinancePartialYearBanner({ monthsWithData, sx }) {
  if (monthsWithData == null || !Number.isFinite(Number(monthsWithData)) || Number(monthsWithData) >= 12) {
    return null;
  }
  return (
    <Alert severity="warning" variant="outlined" sx={{ mb: 2, ...sx }}>
      {FINANCE_COPY.partialYear(Number(monthsWithData))}
    </Alert>
  );
}

/**
 * Um único aviso de regime (evita empilhar 2–3 alerts).
 * priority: fallback > stale > same (same só se showSame).
 */
export function FinanceBasisStatusAlert({
  basisFallback,
  dualReady,
  likelyIdentical,
  showSame = false,
  sx,
}) {
  if (basisFallback) {
    return (
      <Alert severity="warning" sx={{ mb: 2, ...sx }}>
        {FINANCE_COPY.basisFallback}
      </Alert>
    );
  }
  if (dualReady === false) {
    return (
      <Alert severity="warning" sx={{ mb: 2, ...sx }}>
        {FINANCE_COPY.dualBasisStale}
      </Alert>
    );
  }
  if (showSame && dualReady && likelyIdentical) {
    return (
      <Alert severity="info" variant="outlined" sx={{ mb: 2, ...sx }}>
        {FINANCE_COPY.dualBasisSame}
      </Alert>
    );
  }
  return null;
}

export function FinancePanelIntro({ title, description, syncedAt, extras, ai }) {
  const synced = formatSyncedAt(syncedAt);
  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ sm: "flex-start" }}
        justifyContent="space-between"
      >
        <Box sx={{ minWidth: 0, maxWidth: 640 }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {title ? (
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: description ? 0.25 : 0 }}>
                {title}
              </Typography>
            ) : null}
            {ai ? <FinanceAiIconButton cfg={ai} fontSize="small" /> : null}
          </Stack>
          {description ? (
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640 }}>
              {description}
            </Typography>
          ) : null}
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          {synced ? (
            <Chip size="small" variant="outlined" label={`Atualizado em ${synced}`} />
          ) : null}
          {extras}
        </Stack>
      </Stack>
    </Stack>
  );
}

/**
 * Troca de vista dentro do grupo — pílulas, não uma segunda faixa de abas.
 */
export function FinanceViewSwitch({ value, options, onChange, ariaLabel }) {
  return (
    <Stack
      direction="row"
      spacing={0.75}
      flexWrap="wrap"
      useFlexGap
      role="tablist"
      aria-label={ariaLabel}
      sx={{ py: 0.5 }}
    >
      {(options || []).map((opt) => {
        const selected = opt.key === value;
        return (
          <Chip
            key={opt.key}
            size="small"
            clickable
            role="tab"
            aria-selected={selected}
            color={selected ? "primary" : "default"}
            variant={selected ? "filled" : "outlined"}
            label={opt.label}
            onClick={() => onChange(opt.key)}
            sx={{ fontWeight: selected ? 700 : 600 }}
          />
        );
      })}
    </Stack>
  );
}

/**
 * Card KPI no padrão do console (AnalyticsKpiCard — Passagens / analytics).
 * Aceita `label` (alias de title) e `hint` (vira details) para migração suave.
 * `ai` = cfg para FinanceAiIconButton (mesmo padrão do dashboard).
 */
export function FinanceKpiCard({
  label,
  title,
  value,
  hint,
  details,
  tone = "default",
  accentColor,
  icon,
  periodLabel,
  onClick,
  action,
  ai,
}) {
  const accent =
    accentColor ||
    (tone === "positive"
      ? FINANCE_KPI_ACCENT.positive
      : tone === "negative"
        ? FINANCE_KPI_ACCENT.negative
        : FINANCE_KPI_ACCENT.default);
  const detailLines = Array.isArray(details) ? details : hint ? [hint] : [];
  const aiAction = ai ? <FinanceAiIconButton cfg={ai} /> : null;
  return (
    <AnalyticsKpiCard
      title={title || label}
      value={value}
      accentColor={accent}
      icon={icon || TrendingUpIcon}
      details={detailLines}
      periodLabel={periodLabel}
      onClick={onClick}
      action={action || aiAction}
    />
  );
}

export const FinanceKpiGrid = AnalyticsKpiGrid;

export function FinanceSection({ title, hint, loading, children, sx, ai, action }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2, ...sx }}>
      {(title || ai || action) && (
        <Stack direction="row" alignItems="flex-start" spacing={0.5} sx={{ mb: hint ? 0.5 : 1.25 }}>
          {title ? (
            <Typography variant="subtitle2" sx={{ flex: 1, minWidth: 0, fontWeight: 700 }}>
              {title}
            </Typography>
          ) : (
            <Box sx={{ flex: 1 }} />
          )}
          {ai ? <FinanceAiIconButton cfg={ai} fontSize="small" /> : null}
          {action || null}
        </Stack>
      )}
      {hint ? (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mb: 2, maxWidth: 720 }}
        >
          {hint}
        </Typography>
      ) : null}
      {loading ? (
        <Box sx={{ mb: 1.5 }}>
          <CircularProgress size={18} />
        </Box>
      ) : null}
      {children}
    </Paper>
  );
}

export function FinanceSectionTitle({ children, hint, ai }) {
  return (
    <Box sx={{ mb: 1 }}>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: 0.2, flex: 1 }}>
          {children}
        </Typography>
        {ai ? <FinanceAiIconButton cfg={ai} fontSize="small" /> : null}
      </Stack>
      {hint ? (
        <Typography variant="caption" color="text.secondary" display="block">
          {hint}
        </Typography>
      ) : null}
    </Box>
  );
}

export function FinanceTableShell({ children, maxHeight, title, ai }) {
  const table = (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{
        borderRadius: 2,
        maxHeight: maxHeight || undefined,
        "& .MuiTableCell-head": {
          fontWeight: 700,
          bgcolor: "action.hover",
          whiteSpace: "nowrap",
        },
        "& .MuiTableCell-root": {
          borderColor: "divider",
        },
      }}
    >
      {children}
    </TableContainer>
  );
  if (!title && !ai) return table;
  return (
    <Stack spacing={0.75} sx={{ minWidth: 0, flex: 1 }}>
      {(title || ai) && (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {title ? (
            <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
              {title}
            </Typography>
          ) : (
            <Box sx={{ flex: 1 }} />
          )}
          {ai ? <FinanceAiIconButton cfg={ai} fontSize="small" /> : null}
        </Stack>
      )}
      {table}
    </Stack>
  );
}

export function FinanceAlertStack({ error, success, info, onCloseSuccess, onCloseInfo }) {
  const hasAny = Boolean(error || success || info);
  return (
    <Stack spacing={1.5} sx={{ mb: hasAny ? 0.5 : 0 }}>
      {error ? <Alert severity="error">{error}</Alert> : null}
      {info ? (
        <Alert severity="info" onClose={onCloseInfo}>
          {info}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" onClose={onCloseSuccess}>
          {success}
        </Alert>
      ) : null}
    </Stack>
  );
}

export function moneyTone(cents) {
  const n = Number(cents);
  if (!Number.isFinite(n) || n === 0) return "default";
  return n > 0 ? "positive" : "negative";
}

export function moneyAccent(cents) {
  const tone = moneyTone(cents);
  if (tone === "positive") return FINANCE_KPI_ACCENT.positive;
  if (tone === "negative") return FINANCE_KPI_ACCENT.negative;
  return FINANCE_KPI_ACCENT.default;
}

const MONTH_NAMES_PT = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

export function currentYearMonthLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** Último dia do mês YYYY-MM → YYYY-MM-DD */
export function lastDayOfYearMonth(ym) {
  const m = /^(\d{4})-(\d{2})$/.exec(String(ym || "").trim());
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const last = new Date(year, month, 0).getDate();
  return `${m[1]}-${m[2]}-${String(last).padStart(2, "0")}`;
}

export function isYearCompetencia(value) {
  return /^\d{4}$/.test(String(value || "").trim());
}

export function isMonthCompetencia(value) {
  return /^\d{4}-\d{2}$/.test(String(value || "").trim());
}

/** Normaliza competência para YYYY (ano) ou YYYY-MM (mês). */
export function parseCompetencia(value) {
  const raw = String(value || "").trim();
  if (isYearCompetencia(raw)) {
    return { scope: "year", year: Number(raw), month: null, value: raw };
  }
  if (isMonthCompetencia(raw)) {
    const parts = parseYmParts(raw);
    return {
      scope: "month",
      year: parts.year,
      month: parts.month,
      value: raw,
    };
  }
  const cur = currentYearMonthLocal();
  const parts = parseYmParts(cur);
  return { scope: "month", year: parts.year, month: parts.month, value: cur };
}

export function formatCompetenciaLabel(ymOrYear) {
  const raw = String(ymOrYear || "").trim();
  if (isYearCompetencia(raw)) return `Ano ${raw}`;
  const m = /^(\d{4})-(\d{2})$/.exec(raw);
  if (!m) return String(ymOrYear || "—");
  const monthIdx = Number(m[2]) - 1;
  if (monthIdx < 0 || monthIdx > 11) return String(ymOrYear);
  return `${MONTH_NAMES_PT[monthIdx]} ${m[1]}`;
}

export function shiftYearMonth(ym, deltaMonths) {
  const m = /^(\d{4})-(\d{2})$/.exec(String(ym || "").trim());
  if (!m) return ym;
  const d = new Date(Number(m[1]), Number(m[2]) - 1 + Number(deltaMonths), 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function parseYmParts(ym) {
  const m = /^(\d{4})-(\d{2})$/.exec(String(ym || "").trim());
  if (!m) return null;
  return { year: Number(m[1]), month: Number(m[2]) };
}

function clampYearMonth(year, month) {
  const current = parseYmParts(currentYearMonthLocal());
  let y = Number(year);
  let mo = Number(month);
  if (!Number.isFinite(y) || !Number.isFinite(mo)) return currentYearMonthLocal();
  if (mo < 1) mo = 1;
  if (mo > 12) mo = 12;
  if (current) {
    if (y > current.year || (y === current.year && mo > current.month)) {
      return currentYearMonthLocal();
    }
  }
  return `${y}-${String(mo).padStart(2, "0")}`;
}

function clampYear(year) {
  const current = parseYmParts(currentYearMonthLocal());
  let y = Number(year);
  if (!Number.isFinite(y)) return String(current?.year || new Date().getFullYear());
  if (current && y > current.year) y = current.year;
  if (y < 2000) y = 2000;
  return String(y);
}

function buildSelectableYears(yearsBack = 10) {
  const current = parseYmParts(currentYearMonthLocal());
  const cy = current?.year || new Date().getFullYear();
  const out = [];
  for (let y = cy; y >= cy - yearsBack; y -= 1) out.push(y);
  return out;
}

function monthsOfYear(year) {
  const current = parseYmParts(currentYearMonthLocal());
  const list = [];
  for (let mo = 1; mo <= 12; mo += 1) {
    if (current && year === current.year && mo > current.month) break;
    list.push(`${year}-${String(mo).padStart(2, "0")}`);
  }
  return list;
}

/**
 * Seletor de competência: YYYY-MM (mês) ou YYYY (ano inteiro).
 */
export function FinanceCompetenciaPicker({ value, onChange, disabled = false, label = "Período" }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const currentYm = currentYearMonthLocal();
  const currentYear = String(parseYmParts(currentYm)?.year || new Date().getFullYear());
  const parsed = parseCompetencia(value);
  const isYearScope = parsed.scope === "year";
  const ym = isYearScope ? `${parsed.year}-01` : parsed.value;
  const parts = isYearScope
    ? { year: parsed.year, month: 1 }
    : parseYmParts(parsed.value) || parseYmParts(currentYm);
  const isCurrentMonth = !isYearScope && parsed.value === currentYm;
  const isCurrentYearScope = isYearScope && String(parsed.year) === currentYear;
  const canGoNextMonth = !isYearScope && parsed.value < currentYm;
  const canGoNextYear = parts.year < Number(currentYear);
  const years = useMemo(() => buildSelectableYears(12), []);
  const [browseYear, setBrowseYear] = useState(parts.year);

  const openMenu = (e) => {
    setBrowseYear(parts.year);
    setAnchorEl(e.currentTarget);
  };

  const monthsInBrowseYear = useMemo(() => monthsOfYear(browseYear), [browseYear]);

  const goPrev = () => {
    if (disabled) return;
    if (isYearScope) {
      onChange?.(clampYear(parts.year - 1));
      return;
    }
    onChange?.(shiftYearMonth(parsed.value, -1));
  };
  const goNext = () => {
    if (disabled) return;
    if (isYearScope) {
      if (!canGoNextYear) return;
      onChange?.(clampYear(parts.year + 1));
      return;
    }
    if (!canGoNextMonth) return;
    onChange?.(shiftYearMonth(parsed.value, 1));
  };
  const goPrevYear = () => {
    if (disabled) return;
    if (isYearScope) {
      onChange?.(clampYear(parts.year - 1));
      return;
    }
    onChange?.(clampYearMonth(parts.year - 1, parts.month));
  };
  const goNextYear = () => {
    if (disabled || !canGoNextYear) return;
    if (isYearScope) {
      onChange?.(clampYear(parts.year + 1));
      return;
    }
    const next = clampYearMonth(parts.year + 1, parts.month);
    if (next !== parsed.value) onChange?.(next);
  };

  const selectBrowseYearKeepScope = (year) => {
    setBrowseYear(year);
    if (isYearScope) {
      onChange?.(clampYear(year));
    } else {
      onChange?.(clampYearMonth(year, parts.month));
    }
  };

  const displayLabel = isYearScope ? `Ano ${parts.year}` : formatCompetenciaLabel(parsed.value);
  const displaySecondary = isYearScope
    ? `${parts.year}${isCurrentYearScope ? " · ano atual" : ""}`
    : `${parsed.value}${isCurrentMonth ? " · mês atual" : ` · ${parts.year}`}`;

  return (
    <Stack spacing={0.5} sx={{ minWidth: { xs: "100%", sm: 300 } }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 600, letterSpacing: 0.04, textTransform: "uppercase", fontSize: "0.68rem", pl: 0.25 }}
      >
        {label}
      </Typography>
      <Paper
        variant="outlined"
        sx={{
          display: "flex",
          alignItems: "center",
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: "background.paper",
          borderColor: "divider",
          opacity: disabled ? 0.65 : 1,
        }}
      >
        <Tooltip title="Ano anterior">
          <span>
            <IconButton
              size="small"
              onClick={goPrevYear}
              disabled={disabled}
              aria-label="Ano anterior"
              sx={{ borderRadius: 0, px: 0.5 }}
            >
              <ChevronLeftIcon fontSize="small" />
              <ChevronLeftIcon fontSize="small" sx={{ ml: -1.25, opacity: 0.55 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={isYearScope ? "Ano anterior" : "Mês anterior"}>
          <span>
            <IconButton
              size="small"
              onClick={goPrev}
              disabled={disabled}
              aria-label={isYearScope ? "Ano anterior" : "Mês anterior"}
              sx={{ borderRadius: 0, px: 0.75 }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Divider orientation="vertical" flexItem />

        <ButtonBase
          disabled={disabled}
          onClick={openMenu}
          aria-haspopup="listbox"
          aria-expanded={open ? "true" : undefined}
          aria-label={`${label}: ${displayLabel}`}
          sx={{
            flex: 1,
            minWidth: 0,
            px: 1.25,
            py: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            justifyContent: "flex-start",
            textAlign: "left",
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1.25,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.22 : 0.1),
              color: "primary.main",
              flexShrink: 0,
            }}
          >
            <CalendarMonthOutlinedIcon sx={{ fontSize: 16 }} aria-hidden />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="body2"
              fontWeight={700}
              noWrap
              sx={{ lineHeight: 1.25, fontVariantNumeric: "tabular-nums" }}
            >
              {displayLabel}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: "0.68rem" }}>
              {displaySecondary}
            </Typography>
          </Box>
          {isYearScope ? (
            <Chip size="small" color="secondary" variant="outlined" label="Ano" sx={{ height: 22, flexShrink: 0 }} />
          ) : isCurrentMonth ? (
            <Chip size="small" color="primary" variant="outlined" label="Atual" sx={{ height: 22, flexShrink: 0 }} />
          ) : null}
        </ButtonBase>

        <Divider orientation="vertical" flexItem />

        <Tooltip
          title={
            isYearScope
              ? canGoNextYear
                ? "Próximo ano"
                : "Já está no ano atual"
              : canGoNextMonth
                ? "Próximo mês"
                : "Já está no mês atual"
          }
        >
          <span>
            <IconButton
              size="small"
              onClick={goNext}
              disabled={disabled || (isYearScope ? !canGoNextYear : !canGoNextMonth)}
              aria-label={isYearScope ? "Próximo ano" : "Próximo mês"}
              sx={{ borderRadius: 0, px: 0.75 }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={canGoNextYear ? "Próximo ano" : "Já está no ano atual"}>
          <span>
            <IconButton
              size="small"
              onClick={goNextYear}
              disabled={disabled || !canGoNextYear}
              aria-label="Próximo ano"
              sx={{ borderRadius: 0, px: 0.5 }}
            >
              <ChevronRightIcon fontSize="small" sx={{ opacity: 0.55 }} />
              <ChevronRightIcon fontSize="small" sx={{ ml: -1.25 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { minWidth: 300, maxHeight: 480, borderRadius: 2, mt: 0.5 } }}
      >
        <MenuItem
          dense
          selected={isCurrentMonth}
          onClick={() => {
            onChange?.(currentYm);
            setAnchorEl(null);
          }}
        >
          <ListItemIcon>
            <TodayOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Ir para o mês atual"
            secondary={formatCompetenciaLabel(currentYm)}
            primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
            secondaryTypographyProps={{ variant: "caption" }}
          />
        </MenuItem>
        <MenuItem
          dense
          selected={isYearScope && String(browseYear) === String(parts.year)}
          onClick={() => {
            onChange?.(clampYear(browseYear));
            setAnchorEl(null);
          }}
        >
          <ListItemIcon>
            {isYearScope ? <CheckIcon fontSize="small" color="primary" /> : <CalendarMonthOutlinedIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText
            primary="Ano inteiro"
            secondary={`Todos os meses de ${browseYear}`}
            primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
            secondaryTypographyProps={{ variant: "caption" }}
          />
        </MenuItem>
        <Divider />

        <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}
          >
            Ano
          </Typography>
          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75} sx={{ mt: 0.75 }}>
            {years.map((y) => {
              const selected = y === browseYear;
              const isValueYear = y === parts.year;
              return (
                <Chip
                  key={y}
                  size="small"
                  label={y}
                  color={selected ? "primary" : "default"}
                  variant={selected || isValueYear ? "filled" : "outlined"}
                  onClick={() => selectBrowseYearKeepScope(y)}
                  sx={{ fontWeight: selected || isValueYear ? 700 : 500 }}
                />
              );
            })}
          </Stack>
        </Box>

        <Divider sx={{ my: 1 }} />

        <Box sx={{ px: 1.5, pb: 0.5 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}
          >
            Meses de {browseYear}
          </Typography>
        </Box>
        {monthsInBrowseYear.map((item) => {
          const selected = !isYearScope && item === parsed.value;
          const mo = Number(String(item).slice(5, 7));
          return (
            <MenuItem
              key={item}
              dense
              selected={selected}
              onClick={() => {
                onChange?.(item);
                setAnchorEl(null);
              }}
            >
              <ListItemIcon sx={{ minWidth: 28 }}>
                {selected ? <CheckIcon fontSize="small" color="primary" /> : null}
              </ListItemIcon>
              <ListItemText
                primary={MONTH_NAMES_PT[mo - 1]}
                secondary={item === currentYm ? `${item} · atual` : item}
                primaryTypographyProps={{ variant: "body2", fontWeight: selected ? 700 : 500 }}
                secondaryTypographyProps={{ variant: "caption" }}
              />
            </MenuItem>
          );
        })}
        {!monthsInBrowseYear.length ? (
          <MenuItem dense disabled>
            <ListItemText primary="Nenhum mês disponível neste ano" />
          </MenuItem>
        ) : null}

        <Divider />
        <Box sx={{ px: 2, py: 1.25 }}>
          <TextField
            size="small"
            type="month"
            fullWidth
            label="Outra competência"
            value={isYearScope ? `${parts.year}-${String(Math.min(12, parseYmParts(currentYm)?.month || 1)).padStart(2, "0")}` : ym}
            onChange={(e) => {
              const next = e.target.value;
              if (/^\d{4}-\d{2}$/.test(next)) {
                onChange?.(next > currentYm ? currentYm : next);
                setAnchorEl(null);
              }
            }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: currentYm, lang: "pt-BR" }}
          />
        </Box>
      </Menu>
    </Stack>
  );
}
